import { AgentRowLabel } from "@/components/harness/agentRows";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AGENT_META,
  AGENT_ORDER,
  AgentId,
  findAgent,
} from "@/constants/agents";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { HarnessControl } from "../hooks/useHarnessControl";
import { PermissionModeControl } from "../hooks/usePermissionModeControl";
import { ConfirmationModeButton } from "./ConfirmationModeButton";
import { FOOTER_TRIGGER_CLASS, MENU_CLASS, ROW_CLASS } from "./menuStyles";

/**
 * The line under the composer: permissions on the left, where the chat runs on the right. The
 * chip is the agent switch until the first message and a plain statement after it.
 */
export function ComposerFooterLine({
  permission,
  harness,
  onConnect,
  explain = true,
}: {
  /** Whether Allow all spells out what it means; narrow composers keep only the label. */
  explain?: boolean;
  permission: PermissionModeControl;
  harness: HarnessControl;
  onConnect: (agent: AgentId) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-1 pt-1.5 text-xs">
      <ConfirmationModeButton control={permission} explain={explain} />
      <AgentChip control={harness} onConnect={onConnect} />
    </div>
  );
}

function AgentChip({
  control,
  onConnect,
}: {
  control: HarnessControl;
  onConnect: (agent: AgentId) => void;
}) {
  const [open, setOpen] = useState(false);
  const meta = AGENT_META[control.agent];
  const label = control.connected
    ? meta.chipLabel
    : (meta.notConnectedLabel ?? meta.chipLabel);
  const inner = (
    <>
      <meta.Logo className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </>
  );

  // Until this load's catalog lands the agent is not known; saying Cohesive would be a guess.
  if (control.pending) {
    return (
      <span
        role="status"
        aria-busy
        aria-label="Where this chat runs"
        className="inline-flex h-5 w-20 shrink-0 animate-pulse rounded-md bg-n-10"
      />
    );
  }

  if (!control.switchable) {
    return (
      <span
        className="inline-flex min-w-0 shrink-0 items-center gap-1.5 px-1.5 py-0.5 text-n-50"
        aria-label={`Runs on ${label}`}
      >
        {inner}
      </span>
    );
  }

  const pick = (agent: AgentId) => {
    if (agent !== control.agent) {
      trackComposerControlChanged({ control: "agent", value: agent });
    }
    control.setAgent(agent);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Where this chat runs: ${label}`}
          className={cn(
            FOOTER_TRIGGER_CLASS,
            "shrink-0 text-n-50 hover:bg-accent hover:text-n-90",
          )}
        >
          {inner}
          <ChevronDown className="size-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className={cn(MENU_CLASS, "w-80")}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-4 pt-2 pb-1 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
          Where this chat runs
        </div>
        {AGENT_ORDER.map((agent) => {
          const capability = findAgent(control.agents, agent);
          if (!capability) return null;
          const selected = agent === control.agent;
          return (
            <div
              key={agent}
              role="button"
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                pick(agent);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setOpen(false);
                  pick(agent);
                }
              }}
              className={cn(
                ROW_CLASS,
                "min-h-11 py-1.5 text-sm",
                selected && "bg-accent/60",
              )}
            >
              <AgentRowLabel
                agent={agent}
                capability={capability}
                onConnect={() => {
                  setOpen(false);
                  pick(agent);
                  onConnect(agent);
                }}
              />
              {selected && (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
            </div>
          );
        })}
        <div className="px-4 pt-2 pb-1 text-xs leading-snug text-muted-foreground">
          Fixed once the chat starts.
        </div>
      </PopoverContent>
    </Popover>
  );
}
